// DispatchDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress
 } from '@mui/material';
 import axios from 'axios';
 import { fetchRankedSPsForJob } from '../../services/jobDashboardService';
 import { useUser } from '../../context/UserContext';
 import { getUserOrganizationLevel } from '../../utils/permissionStore';
 import useAvailableDrivers from '../../hooks/useAvailableDrivers'; // Import the hook

 // Remove 'drivers' prop, add 'selectedJob'
 const DispatchDialog = ({ open, onClose, onAssign, loading, selectedJob }) => {
   const { currentUser } = useUser();
   const [selectedDriver, setSelectedDriver] = useState(null);
   const [selectedSP, setSelectedSP] = useState(null);
   const [rankedSPs, setRankedSPs] = useState([]);
   const [loadingSPs, setLoadingSPs] = useState(false);
   const [selectedVehicle, setSelectedVehicle] = useState(null);
   // Trucks the selected driver can take: unassigned ones plus their own
   const [availableVehicles, setAvailableVehicles] = useState([]);
   const [vehiclesLoading, setVehiclesLoading] = useState(false);


   // *** Use the hook internally to get drivers, filtered by jobZip ***
   const jobZipForDriverFilter = selectedJob?.serviceLocation?.zip;
   const {
       availableDrivers, // Use drivers from the hook
       isLoading: driversLoading, // Rename to avoid conflict
       error: driversError
   } = useAvailableDrivers(open, jobZipForDriverFilter); // Pass open flag and jobZip

   // *** Log availableDrivers on each render ***
   // console.log('[DispatchDialog Render] availableDrivers:', availableDrivers);

   // Reset state and fetch SPs when dialog is opened
   useEffect(() => {
     if (open) {
       setSelectedDriver(null);
       setSelectedSP(null);
       setSelectedVehicle(null);
       setRankedSPs([]); // Clear previous SPs

       const isSPUser = currentUser?.primaryRole === 'SP';

       // Log the selectedJob data when the dialog opens
       // console.log('[DispatchDialog] useEffect - selectedJob data:', JSON.stringify(selectedJob, null, 2));

       // Pass the whole selectedJob object if it exists and has necessary details
       if (selectedJob?.id && selectedJob?.serviceLocation?.zip && selectedJob?.service && selectedJob?.classType && !isSPUser) {
         // console.log('[DispatchDialog] Fetching ranked SPs with job details:', selectedJob); // Log before fetch
         setLoadingSPs(true);
         fetchRankedSPsForJob(selectedJob)
           .then(data => {
             // console.log('[DispatchDialog] Received ranked SPs:', data); // Log received data
             setRankedSPs(data);
             setLoadingSPs(false);
           })
           .catch(err => {
             console.error("[DispatchDialog] Error fetching ranked SPs:", err);
             setRankedSPs([]); // Clear on error
             setLoadingSPs(false);
           });
       } else if (selectedJob?.id && isSPUser) {
         setLoadingSPs(false);
         // console.log("[DispatchDialog] SP user - skipping ranked SPs fetch.");
       } else {
         // console.warn("[DispatchDialog] Conditions not met for fetching ranked SPs:", {
         //     hasId: !!selectedJob?.id,
         //     hasZip: !!selectedJob?.serviceLocation?.zip,
         //     hasService: !!selectedJob?.service,
         //     hasClassType: !!selectedJob?.classType,
         //     isSPUser: isSPUser
         // });
         setLoadingSPs(false);
         setRankedSPs([]);
       }
     }
   }, [open, selectedJob, currentUser]); // Add currentUser as dependency

   const handleSelectDriver = async (driver) => {
     setSelectedDriver(driver);
     setSelectedSP(null);
     setSelectedVehicle(null);
     setAvailableVehicles([]);
     if (!driver) return;

     const driverId = String(driver._id || driver.id);
     setVehiclesLoading(true);
     try {
       const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/vehicles/available`, {
         params: { driverId },
         headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
       });
       const vehicles = Array.isArray(response.data) ? response.data : [];
       setAvailableVehicles(vehicles);
       const ownVehicle = vehicles.find(vehicle => vehicle.driverId && String(vehicle.driverId) === driverId);
       if (ownVehicle) setSelectedVehicle(ownVehicle);
     } catch (err) {
       console.error('[DispatchDialog] Error loading vehicles:', err);
     } finally {
       setVehiclesLoading(false);
     }
   };

   const handleAssign = () => {
    if (!selectedDriver && !selectedSP) {
       alert('Please select a Driver or Service Provider.');
       return;
     }

    const isSPUser = currentUser?.primaryRole === 'SP';
    if (!isSPUser && selectedDriver && !selectedVehicle) {
      alert('Please select a vehicle for the driver.');
      return;
    }

     const userToAssign = selectedDriver || selectedSP;
     let vehicleIdentifier;
     if (selectedDriver) {
       if (isSPUser) {
         console.log("SP user assigning their own driver - using auto vehicle assignment");
         vehicleIdentifier = "SP_AUTO_ASSIGN";
       } else {
         vehicleIdentifier = selectedVehicle?._id || selectedVehicle?.name;
       }
     } else {
       vehicleIdentifier = null;
     }
     onAssign(userToAssign, vehicleIdentifier);
    };

   // *** REMOVED useMemo for filteredDrivers - use availableDrivers directly ***
   // const filteredDrivers = useMemo(() => { ... }, [availableDrivers, currentUser]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>Dispatch Job</DialogTitle>
       <DialogContent>
         <Grid container spacing={2}>
           {/* Driver Selection Column */}
           <Grid item xs={12} md={4}>
             <Typography variant="subtitle1" gutterBottom>
               Assign Driver
             </Typography>
             <Box sx={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
               {/* Use driversLoading from the hook */}
               {driversLoading ? (
                 <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
               ) : driversError ? (
                 <Typography variant="body2" color="error">Error: {driversError}</Typography>
               // *** Directly check availableDrivers length ***
               ) : availableDrivers && availableDrivers.length > 0 ? (
                 // *** Directly map availableDrivers ***
                 availableDrivers.map((driver) => (
                   <Box
                     key={driver.id}
                     sx={{
                       p: 1, mb: 1, border: '1px solid #ddd', borderRadius: 1, cursor: 'pointer',
                       bgcolor: selectedDriver?.id === driver.id ? 'primary.light' : 'background.paper',
                       '&:hover': { bgcolor: 'action.hover' },
                     }}
                     onClick={() => handleSelectDriver(driver)}
                   >
                     <Typography variant="body1">{driver.name}</Typography>
                     <Typography variant="caption" color="text.secondary">
                       {driver.phone || 'No phone'} - {driver.status || 'Unknown Status'}
                     </Typography>
                   </Box>
                 ))
               ) : (
                 <Typography variant="body2" color="text.secondary">
                   No on-duty drivers available{jobZipForDriverFilter ? ' within 70 miles' : ''}.
                 </Typography>
               )}
             </Box>
           </Grid>

           {/* SP Selection Column - Only show for Org 1 users */}
           {(() => {
             const orgLevel = currentUser ? getUserOrganizationLevel(currentUser) : null;
             const isOrg1User = orgLevel === 1;
             return isOrg1User;
           })() && (
             <Grid item xs={12} md={4}>
               <Typography variant="subtitle1" gutterBottom>
                 Assign Service Provider
               </Typography>
               <Box sx={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                 {loadingSPs ? (
                   <CircularProgress size={20} />
                 ) : rankedSPs && rankedSPs.length > 0 ? (
                   rankedSPs.map((sp) => (
                     <Box
                       key={sp.id}
                       sx={{
                         p: 1, mb: 1, border: '1px solid #ddd', borderRadius: 1, cursor: 'pointer',
                         bgcolor: selectedSP?.id === sp.id ? 'primary.light' : 'background.paper',
                         '&:hover': { bgcolor: 'action.hover' },
                       }}
                       onClick={() => { setSelectedSP(sp); setSelectedDriver(null); setSelectedVehicle(null); }}
                     >
                       <Typography variant="body1">{sp.companyName || sp.name}</Typography>
                       <Typography variant="caption" color="text.secondary">
                         En Route: {sp.totalEnRouteDistance ?? 'N/A'} mi
                         {sp.totalLoadedDistance !== null && ` | Loaded: ${sp.totalLoadedDistance} mi`}
                         {' | Est. Cost: $'}
                         {sp.estimatedTotalCost !== null ? sp.estimatedTotalCost.toFixed(2) : 'N/A'}
                       </Typography>
                     </Box>
                   ))
                 ) : (
                   <Typography variant="body2" color="text.secondary">
                     No eligible SPs found
                   </Typography>
                 )}
               </Box>
             </Grid>
           )}

           {/* Truck Selection Column */}
           <Grid item xs={12} md={4}>
             <Typography variant="subtitle1" gutterBottom>
               Select Truck (Optional)
             </Typography>
             <>
                {selectedVehicle && selectedDriver && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Driver {selectedDriver.name} is assigned to vehicle {selectedVehicle.name}
                  </Alert>
                )}
                {!selectedVehicle && availableVehicles.length === 0 && !vehiclesLoading && selectedDriver && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No trucks available for this driver. Add one on the Fleet page, or free one up (trucks marked Maintenance or Out of Service, or assigned to another driver, can't be used).
                  </Alert>
                )}
                 <FormControl fullWidth disabled={!!selectedSP || !selectedDriver}>
                   <InputLabel id="vehicle-select-label">Vehicle</InputLabel>
                   <Select
                     labelId="vehicle-select-label"
                     value={selectedVehicle ? selectedVehicle._id : ''}
                     onChange={(e) => {
                       setSelectedVehicle(availableVehicles.find(v => v._id === e.target.value) || null);
                     }}
                     label="Vehicle"
                   >
                     {availableVehicles.map((vehicle) => (
                       <MenuItem key={vehicle._id} value={vehicle._id}>
                         {vehicle.name} - {vehicle.type || 'Unknown type'}
                         {vehicle.driverId && String(vehicle.driverId) === String(selectedDriver?._id || selectedDriver?.id) ? ' (Assigned)' : ''}
                       </MenuItem>
                     ))}
                   </Select>
                 </FormControl>
                 {!!selectedSP && (
                    <Typography variant="caption" color="text.secondary" sx={{mt: 1}}>
                        Truck assignment is handled by the Service Provider.
                    </Typography>
                 )}
              </>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
           <Button
             onClick={handleAssign}
             variant="contained"
             color="primary"
             // Update disabled check to use driversLoading from hook
             disabled={(!selectedDriver && !selectedSP) || loading || vehiclesLoading || loadingSPs || driversLoading}
           >
             {selectedSP ? 'Assign to SP' : 'Dispatch to Driver'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DispatchDialog;
